"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    createUserWithEmailAndPassword,
    getMultiFactorResolver,
    inMemoryPersistence,
    multiFactor,
    MultiFactorResolver,
    MultiFactorUser,
    PhoneAuthProvider,
    PhoneInfoOptions,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier,
    sendEmailVerification,
    signInWithEmailAndPassword,
    signOut,
    User,
    AuthErrorCodes,
    sendPasswordResetEmail,
} from "firebase/auth";
import { firebaseAuth, setSessionCookie } from "@/lib/api/firebase-client";
import { useRouter } from "next/navigation";
import { setFirebaseFormikErrors, VerifyPayload } from "@/app/(auth)/data";
import { toast } from "sonner";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { notifyError, notifySuccess } from "@/ui/elements/Toasts";
import { getTenantInfo } from "@/lib/theme/tenant";
import * as Sentry from "@sentry/nextjs";

type AuthPayload = { email: string; password: string };
interface AuthHelpers {
    setFieldError(field: string, value: string): void;
}
type VerifyPhoneFn = (payload: VerifyPayload) => Promise<void>;
type StartAuthFn = (
    payload: AuthPayload,
    helpers: AuthHelpers,
) => Promise<void>;

export type FirebaseAuthContextType = {
    user: User | null;
    multi: MultiFactorUser | null;
    verify: boolean;
    beginEnrollPhone: VerifyPhoneFn;
    finishEnrollPhone: VerifyPhoneFn;
    beginLogin: StartAuthFn;
    beginRegister: StartAuthFn;
    verifyLogin: Function;
    resendVerificationEmail: () => Promise<void>;
    resendPhoneVerification: () => Promise<void>;
    getVerifier: () => RecaptchaVerifier | null;
};

const FirebaseAuthContext = createContext<FirebaseAuthContextType>({
    user: null,
    multi: null,
    verify: false,
    beginEnrollPhone: async () => {},
    finishEnrollPhone: async () => {},
    beginLogin: async () => {},
    beginRegister: async () => {},
    verifyLogin: async () => {},
    resendVerificationEmail: async () => {},
    resendPhoneVerification: async () => {},
    getVerifier() {
        return null;
    },
});

function ignoreReportingForCode(code: AuthErrorCodes) {
    switch (code) {
        case AuthErrorCodes.INVALID_EMAIL: return true
        default: return false
    }
}

export const FirebaseAuthProvider = ({ children }) => {
    const router = useRouter();

    const reContainer = useRef<HTMLDivElement | null>(null);
    const reVerifier = useRef<RecaptchaVerifier | null>(null);
    const reWidgetId = useRef<number | null>(null);

    const [flowId, setFlowId] = useState<number>(Date.now());
    const [user, setUser] = useState<User | null>(null);
    const [phoneVerifier, setPhoneVerifier] = useState<PhoneInfoOptions | null>(
        null,
    );

    const [verifySessionId, setVerifySessionId] = useState<string | null>(null);
    const [verifyResolver, setVerifyResolver] =
        useState<MultiFactorResolver | null>(null);

    useEffect(() => {
        if (reVerifier.current == null) {
            const verifier = new RecaptchaVerifier(
                firebaseAuth(),
                reContainer.current,
                {
                    size: "invisible",
                },
            );
            reVerifier.current = verifier;
            verifier.render().then((widget) => {
                reWidgetId.current = widget;
            });
        }
    }, []);

    useEffect(() => {
        firebaseAuth().onAuthStateChanged((user) => {
            setUser(user);
        });
    }, [setUser]);

    const multi = useMemo(() => {
        if (user) {
            return multiFactor(user);
        } else {
            return null;
        }
    }, [user]);

    // Check to see if the user is logged in and has successfully added their second factor
    // If so - perform the serverside login flow and go to the overview page
    useEffect(() => {
        let latch = false;
        async function run() {
            if (user && multi) {
                if (user.emailVerified && multi.enrolledFactors.length > 0) {
                    const idtoken = await user.getIdToken();
                    if (!latch) {
                        await setSessionCookie(idtoken);
                        router.push("/overview");
                    }
                }
            }
        }
        run();
        return () => {
            latch = true;
        };
    }, [user, multi, router, flowId]);

    const handleFirebaseError = useCallback(
        async (e: Error & { code: AuthErrorCodes }) => {
            switch (e.code) {
                case "auth/requires-recent-login": {
                    setVerifySessionId(null);
                    setVerifyResolver(null);
                    await signOut(firebaseAuth());
                    notifyError(
                        "Your login session has expired, please try again",
                        {},
                        {
                            duration: 7500,
                        },
                    );
                    return true;
                }
                case "auth/invalid-credential": {
                    notifyError(
                        "The email or password you entered was incorrect, please try again",
                        {},
                        {
                            duration: 7500,
                        },
                    );
                    return true;
                }
                case "auth/invalid-verification-code": {
                    notifyError(
                        "The verification code you provided was incorrect, please try again",
                        {},
                        {
                            duration: 7500,
                        },
                    );
                    return true;
                }
                case "bippit/permission-denied": {
                    notifyError(
                        "The account you are trying to access has been disabled",
                        {},
                        {
                            duration: 7500,
                        },
                    );
                    return true;
                }
                case "auth/multi-factor-auth-required": {
                    const resolver = getMultiFactorResolver(firebaseAuth(), e);
                    const hint = resolver.hints.find(
                        (hint) =>
                            hint.factorId ===
                            PhoneMultiFactorGenerator.FACTOR_ID,
                    );
                    if (hint) {
                        const phoneInfo = {
                            multiFactorHint: hint,
                            session: resolver.session,
                        };
                        setPhoneVerifier(phoneInfo);
                        await sendPhoneVerification(phoneInfo);
                        setVerifyResolver(resolver);
                    }
                    return true;
                }
            }

            if (ignoreReportingForCode(e.code)) {
                throw e;
            }

            Sentry.captureException(e, {
                extra: {
                    has_recaptcha_init: reVerifier.current !== null,
                    has_email_verified: user?.emailVerified ?? false,
                    has_started_login: user != null,
                    firebase_code: e.code,
                },
            });

            notifyError(
                "Something went wrong. Please try again shortly",
                {
                    actionText: "Contact Support",
                    href: `mailto:${getTenantInfo().contact}`,
                    icon: FaCheckCircle,
                },
                {
                    duration: 7500,
                },
            );
        },
        [setPhoneVerifier, setVerifyResolver, setVerifySessionId, user],
    );

    const sendPhoneVerification = async (providerInfo: PhoneInfoOptions) => {
        const phoneProvider = new PhoneAuthProvider(firebaseAuth());
        try {
            Sentry.addBreadcrumb({
                category: "auth",
                message: "Sending phone verification code",
                level: "info",
            });
            const verifyId = await phoneProvider.verifyPhoneNumber(
                providerInfo,
                reVerifier.current,
            );
            setVerifySessionId(verifyId);
        } catch (error) {
            await handleFirebaseError(error);
        }
    };

    const resendPhoneVerification = async () => {
        if (!phoneVerifier) {
            const error = new Error(
                "Phone verification expired, try logging in again",
            );
            error.code = "auth/requires-recent-login";
            await handleFirebaseError(error);
            return;
        }

        await sendPhoneVerification(phoneVerifier);
        notifySuccess(
            "We have resent your verification code. Still having trouble?",
            {
                actionText: "Contact Us",
                href: `mailto:${getTenantInfo().contact}`,
                icon: FaCheckCircle,
            },
            {
                duration: 15000,
            },
        );
    };

    const beginLogin = useCallback(
        async ({ email, password }, helpers) => {
            await firebaseAuth().setPersistence(inMemoryPersistence);
            try {
                Sentry.addBreadcrumb({
                    category: "auth",
                    message: "Logging in to firebase",
                    level: "info",
                });
                await signInWithEmailAndPassword(
                    firebaseAuth(),
                    email,
                    password,
                );
            } catch (e) {
                await handleFirebaseError(e);
            }
        },
        [setVerifySessionId],
    );

    const beginRegister = useCallback(
        async ({ email, password }, helpers) => {
            await firebaseAuth().setPersistence(inMemoryPersistence);
            try {
                Sentry.addBreadcrumb({
                    category: "auth",
                    message: "Registering a new account",
                    level: "info",
                });
                const user = await createUserWithEmailAndPassword(
                    firebaseAuth(),
                    email,
                    password,
                );
                Sentry.addBreadcrumb({
                    category: "auth",
                    message: "Sending email verification",
                    level: "info",
                });
                await sendEmailVerification(user.user, getActionCodeSettings());
            } catch (e) {
                await handleFirebaseError(e);
            }
        },
        [setVerifySessionId],
    );

    const verifyLogin = useCallback(
        async ({ verifyCode }, helpers) => {
            if (verifySessionId == null || verifyResolver == null) {
                const error = new Error(
                    "Phone verification expired, try logging in again",
                );
                error.code = "auth/requires-recent-login";
                await handleFirebaseError(error);
                return;
            }

            const credential = PhoneAuthProvider.credential(
                verifySessionId,
                verifyCode,
            );
            const assertion = PhoneMultiFactorGenerator.assertion(credential);

            Sentry.addBreadcrumb({
                category: "auth",
                message: "Verifying login code",
                level: "info",
            });
            try {
                await verifyResolver.resolveSignIn(assertion);
                setVerifySessionId(null);
                setVerifyResolver(null);
                setPhoneVerifier(null);
            } catch (e) {
                await handleFirebaseError(e);
            }
        },
        [verifyResolver, verifySessionId],
    );

    const beginEnrollPhone: VerifyPhoneFn = useCallback(
        async (phoneNumber: string) => {
            if (multi == null) {
                return;
            }
            const session = await multi?.getSession();
            const providerInfo = {
                phoneNumber,
                session,
            };
            setPhoneVerifier(providerInfo);
            try {
                Sentry.addBreadcrumb({
                    category: "auth",
                    message: "Starting phone number enrollment",
                    level: "info",
                });
                await sendPhoneVerification(providerInfo);
            } catch (e) {
                console.error(e);
                notifyError(
                    "The phone number you provided could not be verified, please check your number and try again",
                    {
                        icon: FaTimesCircle,
                    },
                );
            }
        },
        [setVerifySessionId, multi, reVerifier],
    );

    const finishEnrollPhone: VerifyPhoneFn = useCallback(
        async ({ verifyCode }: VerifyPayload) => {
            if (verifySessionId == null || multi == null) {
                const error = new Error(
                    "Phone verification expired, try logging in again",
                );
                error.code = "auth/requires-recent-login";
                await handleFirebaseError(error);
                return;
            }

            const credential = PhoneAuthProvider.credential(
                verifySessionId,
                verifyCode,
            );
            const assertion = PhoneMultiFactorGenerator.assertion(credential);
            try {
                Sentry.addBreadcrumb({
                    category: "auth",
                    message: "Confirming phone number enrollment",
                    level: "info",
                });
                await multi.enroll(assertion);
                setVerifySessionId(null);
                setPhoneVerifier(null);
                setFlowId(Date.now());
            } catch (e) {
                await handleFirebaseError(e);
            }
        },
        [verifySessionId, multi],
    );

    const resendVerificationEmail = useCallback(async () => {
        if (user) {
            try {
                await sendEmailVerification(user, getActionCodeSettings());
            } catch (e) {
                await handleFirebaseError(e);
            }
        }
    }, [user]);

    const sendPasswordReset = useCallback(
        async (email: string) => {
            try {
                await sendPasswordResetEmail(
                    firebaseAuth(),
                    email,
                    getActionCodeSettings(),
                );
            } catch (e) {
                console.error(e);
                await handleFirebaseError(e);
            }
        },
        [handleFirebaseError],
    );

    const providerValue: FirebaseAuthContextType = useMemo(
        () => ({
            user,
            multi,
            verify: verifySessionId != null,
            beginEnrollPhone,
            finishEnrollPhone,
            beginRegister,
            beginLogin,
            verifyLogin,
            sendPasswordReset,
            resendVerificationEmail,
            getVerifier() {
                return reVerifier.current;
            },
            resendPhoneVerification,
        }),
        [
            user,
            multi,
            verifySessionId,
            beginRegister,
            beginLogin,
            verifyLogin,
            sendPasswordReset,
            resendVerificationEmail,
            beginEnrollPhone,
            finishEnrollPhone,
            resendPhoneVerification,
        ],
    );

    return (
        <FirebaseAuthContext.Provider value={providerValue}>
            {children}
            <div id="re-container" ref={reContainer} />
        </FirebaseAuthContext.Provider>
    );
};

export const useFirebaseContext = () => useContext(FirebaseAuthContext);
export const getActionCodeSettings = () => ({
    url: `${window.location.protocol}//${window.location.host}/login`,
});
